/**
 * WebGL mesh gradient renderer — the "Stripe.com" technique.
 * Single full-screen quad + fragment shader that interpolates 8 anchor colors
 * over a noise-warped UV grid. Each anchor has its own hex, opacity (weight),
 * position (x/y in 0–1 space), and size (reach multiplier).
 *
 * Degrades gracefully:
 *  - no `getContext('webgl')` → renders nothing (caller still has CSS base color)
 *  - `prefers-reduced-motion` → renders single static frame
 *  - tab hidden → pauses RAF
 *
 * Motion modes (uMotion):
 *   0 drift  — anchors orbit around their base position
 *   1 swirl  — warped uv is rotated around center over time
 *   2 pulse  — warp intensity oscillates; anchors still
 *   3 still  — frozen (single frame)
 */
import { useEffect, useRef } from "react";
import { MeshConfig, type MeshWebgl } from "@/lib/meshGradient";

const motionToInt = (m: MeshConfig["motion"]): number =>
  m === "swirl" ? 1 : m === "pulse" ? 2 : m === "still" ? 3 : 0;

const VERT = `
attribute vec2 aPos;
varying vec2 vUv;
void main(){
  vUv = aPos * 0.5 + 0.5;
  gl_Position = vec4(aPos, 0.0, 1.0);
}`;

const FRAG = `
precision mediump float;
varying vec2 vUv;
uniform float uTime;
uniform float uIntensity;
uniform int uMotion;
uniform vec3 uColors[8];
uniform float uOpacities[8];
uniform vec2 uPositions[8];
uniform float uSizes[8];

// Cheap 2D simplex-ish noise (Inigo Quilez)
vec2 hash2(vec2 p){
  p = vec2(dot(p, vec2(127.1, 311.7)), dot(p, vec2(269.5, 183.3)));
  return -1.0 + 2.0 * fract(sin(p) * 43758.5453123);
}
float noise(vec2 p){
  vec2 i = floor(p);
  vec2 f = fract(p);
  vec2 u = f * f * (3.0 - 2.0 * f);
  return mix(
    mix(dot(hash2(i + vec2(0.0,0.0)), f - vec2(0.0,0.0)),
        dot(hash2(i + vec2(1.0,0.0)), f - vec2(1.0,0.0)), u.x),
    mix(dot(hash2(i + vec2(0.0,1.0)), f - vec2(0.0,1.0)),
        dot(hash2(i + vec2(1.0,1.0)), f - vec2(1.0,1.0)), u.x),
    u.y);
}

void main(){
  vec2 uv = vUv;

  // Effective time per motion mode
  float t = (uMotion == 3) ? 0.0 : uTime * 0.15;

  // Anchor orbit only when drift
  float anchorT = (uMotion == 0) ? t : 0.0;

  // Pulse mode: oscillate warp intensity
  float pulseFactor = (uMotion == 2)
    ? (0.4 + 0.6 * (0.5 + 0.5 * sin(uTime * 0.8)))
    : 1.0;
  float warp = uIntensity * 0.6 * pulseFactor;

  vec2 wuv = uv + warp * vec2(
    noise(uv * 2.0 + vec2(t, 0.0)),
    noise(uv * 2.0 + vec2(0.0, t))
  );

  // Swirl mode: rotate warped uv around center
  if (uMotion == 1) {
    float a = t * 1.2;
    vec2 c = wuv - 0.5;
    wuv = 0.5 + vec2(c.x * cos(a) - c.y * sin(a), c.x * sin(a) + c.y * cos(a));
  }

  // Blend 8 anchors weighted by inverse distance, opacity, and size.
  vec3 col = vec3(0.0);
  float ws = 0.0;
  for (int i = 0; i < 8; i++) {
    float fi = float(i);
    vec2 base = uPositions[i];
    vec2 p = base + 0.08 * vec2(
      sin(anchorT * (1.0 + fi * 0.13)),
      cos(anchorT * (0.9 + fi * 0.11))
    );
    float d = distance(wuv, p);
    float reach = max(uSizes[i], 0.05);
    float w = uOpacities[i] / (0.05 + d * 2.0 / reach);
    col += uColors[i] * w;
    ws += w;
  }
  // Guard against ws=0 when every anchor opacity is 0
  ws = max(ws, 0.0001);
  gl_FragColor = vec4(col / ws, 1.0);
}`;

const hexToRgb = (hex: string): [number, number, number] => {
  const h = hex.replace("#", "").trim();
  const v = h.length === 3
    ? h.split("").map((c) => c + c).join("")
    : h.padEnd(6, "0").slice(0, 6);
  const n = parseInt(v, 16);
  return [((n >> 16) & 255) / 255, ((n >> 8) & 255) / 255, (n & 255) / 255];
};

type Props = { webgl: MeshWebgl; motion?: MeshConfig["motion"] };

export const WebGLMeshBackground = ({ webgl, motion = "drift" }: Props) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const rafRef = useRef<number | null>(null);
  const webglRef = useRef(webgl);
  webglRef.current = webgl;
  const motionRef = useRef(motion);
  motionRef.current = motion;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const gl = canvas.getContext("webgl", { antialias: true, premultipliedAlpha: false }) as WebGLRenderingContext | null;
    if (!gl) return;

    const compile = (type: number, src: string) => {
      const sh = gl.createShader(type)!;
      gl.shaderSource(sh, src);
      gl.compileShader(sh);
      if (!gl.getShaderParameter(sh, gl.COMPILE_STATUS)) {
        gl.deleteShader(sh);
        return null;
      }
      return sh;
    };
    const vs = compile(gl.VERTEX_SHADER, VERT);
    const fs = compile(gl.FRAGMENT_SHADER, FRAG);
    if (!vs || !fs) return;

    const prog = gl.createProgram()!;
    gl.attachShader(prog, vs);
    gl.attachShader(prog, fs);
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) return;
    gl.useProgram(prog);

    const buf = gl.createBuffer();
    gl.bindBuffer(gl.ARRAY_BUFFER, buf);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW);
    const aPos = gl.getAttribLocation(prog, "aPos");
    gl.enableVertexAttribArray(aPos);
    gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

    const uTime = gl.getUniformLocation(prog, "uTime");
    const uIntensity = gl.getUniformLocation(prog, "uIntensity");
    const uMotion = gl.getUniformLocation(prog, "uMotion");
    const uColors = gl.getUniformLocation(prog, "uColors");
    const uOpacities = gl.getUniformLocation(prog, "uOpacities");
    const uPositions = gl.getUniformLocation(prog, "uPositions");
    const uSizes = gl.getUniformLocation(prog, "uSizes");

    const reduced = typeof window !== "undefined" && window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const start = performance.now();

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      canvas.width = Math.max(1, Math.floor(w * dpr));
      canvas.height = Math.max(1, Math.floor(h * dpr));
      gl.viewport(0, 0, canvas.width, canvas.height);
    };
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    let paused = false;
    const onVis = () => { paused = document.hidden; if (!paused) render(); };
    document.addEventListener("visibilitychange", onVis);

    const colorBuf = new Float32Array(8 * 3);
    const opacityBuf = new Float32Array(8);
    const positionBuf = new Float32Array(8 * 2);
    const sizeBuf = new Float32Array(8);

    const render = () => {
      if (paused) return;
      const cfg = webglRef.current;
      const t = ((performance.now() - start) / 1000) * (cfg.speed / 5);
      gl.uniform1f(uTime, reduced ? 0 : t);
      gl.uniform1f(uIntensity, cfg.intensity);
      gl.uniform1i(uMotion, motionToInt(motionRef.current));
      for (let i = 0; i < 8; i++) {
        const a = cfg.colors[i];
        const rgb = hexToRgb(a?.hex ?? "#000000");
        colorBuf[i * 3] = rgb[0];
        colorBuf[i * 3 + 1] = rgb[1];
        colorBuf[i * 3 + 2] = rgb[2];
        opacityBuf[i] = (a?.enabled === false ? 0 : 1) * (typeof a?.opacity === "number" ? a.opacity : 1);
        positionBuf[i * 2] = typeof a?.x === "number" ? a.x : 0.5;
        // GLSL y=0 is bottom; admin x/y is canvas-space top-down, so flip y for shader.
        positionBuf[i * 2 + 1] = 1 - (typeof a?.y === "number" ? a.y : 0.5);
        sizeBuf[i] = typeof a?.size === "number" ? a.size : 1;
      }
      gl.uniform3fv(uColors, colorBuf);
      gl.uniform1fv(uOpacities, opacityBuf);
      gl.uniform2fv(uPositions, positionBuf);
      gl.uniform1fv(uSizes, sizeBuf);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
      if (!reduced) rafRef.current = requestAnimationFrame(render);
    };
    render();

    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      document.removeEventListener("visibilitychange", onVis);
      ro.disconnect();
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      style={{ position: "absolute", inset: 0, width: "100%", height: "100%", display: "block" }}
    />
  );
};
